import React, { useState } from "react";
import { InputAdornment, IconButton, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Search as SearchIcon } from "@mui/icons-material";
import TuneIcon from "@mui/icons-material/Tune";
import { messages } from "../../../locales/messages";
import { ChangeEvent, useEffect, useRef } from "react";
import { Wrapper, StyledTextField, Title } from "../SearchInputField/styled";
import { ImageKeywordAutocomplete } from "../ImageKeywordAutocomplete";
import { AdvancedSearchConfigModal } from "../AdvancedSearchConfigModal";
import { useAdvancedSearchConfig } from "../../../hooks/useAdvancedSearchConfig";
import { AdvancedSearchConfigDto } from "@swagger/typescript-fetch-goflyer";

interface Props {
  title?: string;
  /**
   * Handles the search action after pressing enter or clicking on the search button.
   */
  onSearch?: (value: string, advancedConfig?: AdvancedSearchConfigDto) => void;
  dataTestId?: string;
  clearSearchText?: boolean;
  placeHolder?: string;
  autoFocus?: boolean;
  initialValue?: string;
  /**
   * Whether to enable keyword suggestions for search (currently for images, may extend to other entities in the future)
   * @default false
   */
  enableKeywordSuggestions?: boolean;
  /**
   * Whether to show the advanced search configuration button
   * @default false
   */
  enableAdvancedSearch?: boolean;
  /**
   * Base z-index used by autocomplete popper and advanced config modal
   */
  zIndex?: number;
}

export function SearchInputWithAdvanced({
  title,
  onSearch,
  dataTestId,
  clearSearchText = false,
  placeHolder,
  autoFocus = false,
  initialValue = "",
  enableKeywordSuggestions = false,
  enableAdvancedSearch = false,
  zIndex = 1300,
}: Props) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState(initialValue);
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>();

  const {
    advancedSearchState,
    updateBoostConfig,
    updateFuzzyConfig,
    updateMatchPhraseConfig,
    updateMatchPhrasePrefixConfig,
    updateMinimumShouldMatch,
    updateScriptScoreConfig,
    updateBoostMode,
    toggleAdvancedSearch,
    updateKeepSearchResults,
    updateSearchLimit,
    updateSearchFlyerItems,
    resetToDefaults,
    getSearchConfig,
  } = useAdvancedSearchConfig();

  const performSearch = () => {
    const advancedConfig = getSearchConfig();
    onSearch && onSearch(searchText, advancedConfig);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      inputRef.current?.blur();
      performSearch();
    }
  };

  const handleAdvancedConfigSave = (
    updatedConfig?: AdvancedSearchConfigDto,
  ) => {
    const configToUse = updatedConfig;
    onSearch && onSearch(searchText, configToUse);
  };

  useEffect(() => {
    if (clearSearchText) {
      setSearchText("");
    }
  }, [clearSearchText]);

  useEffect(() => {
    setSearchText(initialValue);
  }, [initialValue]);

  const searchButton = (
    <IconButton
      onClick={performSearch}
      data-testid={`${dataTestId}-button`}
      sx={{ color: "#fd954f" }}
    >
      <SearchIcon style={{ fontSize: 24 }} />
    </IconButton>
  );

  const advancedButton = enableAdvancedSearch ? (
    <IconButton
      onClick={() => setShowAdvancedModal(true)}
      data-testid={`${dataTestId}-advanced-button`}
      sx={{
        color: advancedSearchState.isEnabled ? "#ff813a" : "#757575",
        backgroundColor: advancedSearchState.isEnabled
          ? "#fff3e0"
          : "transparent",
        borderRadius: "4px",
        "&:hover": {
          backgroundColor: advancedSearchState.isEnabled
            ? "#ffe0b2"
            : "#f5f5f5",
        },
      }}
      title="Advanced Search Configuration"
    >
      <TuneIcon />
    </IconButton>
  ) : null;

  if (enableKeywordSuggestions) {
    return (
      <Box>
        <Box position="relative">
          <ImageKeywordAutocomplete
            title={title}
            placeholder={placeHolder || (t(messages.Search()) as string)}
            initialValue={initialValue}
            autoFocus={autoFocus}
            clearSearchText={clearSearchText}
            dataTestId={dataTestId}
            dataTestIdInput={`${dataTestId}-input`}
            dataTestIdSearchButton={`${dataTestId}-button`}
            onSearch={(value) => {
              const advancedConfig = getSearchConfig();
              onSearch && onSearch(value, advancedConfig);
            }}
            onSuggestionSelect={(value) => {
              const advancedConfig = getSearchConfig();
              onSearch && onSearch(value, advancedConfig);
            }}
            styleVariant="default"
            showSearchButton={true}
            showSuggestions={true}
            onInputChange={(value) => setSearchText(value)}
            popperZIndex={zIndex + 1}
          />
          {advancedButton && (
            <Box
              position="absolute"
              top="50%"
              right={enableAdvancedSearch ? "37px" : "10px"}
              sx={{ transform: "translateY(-50%)", zIndex: 1 }}
            >
              {advancedButton}
            </Box>
          )}
        </Box>

        {enableAdvancedSearch && (
          <AdvancedSearchConfigModal
            open={showAdvancedModal}
            onClose={() => setShowAdvancedModal(false)}
            onSave={(updatedConfig) => {
              setShowAdvancedModal(false);
              handleAdvancedConfigSave(updatedConfig);
            }}
            advancedSearchState={advancedSearchState}
            updateBoostConfig={updateBoostConfig}
            updateFuzzyConfig={updateFuzzyConfig}
            updateMatchPhraseConfig={updateMatchPhraseConfig}
            updateMatchPhrasePrefixConfig={updateMatchPhrasePrefixConfig}
            updateMinimumShouldMatch={updateMinimumShouldMatch}
            updateScriptScoreConfig={updateScriptScoreConfig}
            updateBoostMode={updateBoostMode}
            toggleAdvancedSearch={toggleAdvancedSearch}
            updateKeepSearchResults={updateKeepSearchResults}
            updateSearchLimit={updateSearchLimit}
            updateSearchFlyerItems={updateSearchFlyerItems}
            resetToDefaults={resetToDefaults}
            zIndex={zIndex + 2}
          />
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Wrapper data-testid="searchinput-test-id">
        {title && <Title>{title}</Title>}
        <StyledTextField
          inputRef={inputRef}
          autoFocus={autoFocus}
          data-testid={dataTestId}
          id="search-input"
          placeholder={
            placeHolder ? placeHolder : (t(messages.Search()) as string)
          }
          fullWidth
          value={searchText}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const text = e.target.value;
            setSearchText(text);
          }}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box display="flex" alignItems="center" gap={0.5}>
                  {advancedButton}
                  {searchButton}
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </Wrapper>

      {enableAdvancedSearch && (
        <AdvancedSearchConfigModal
          open={showAdvancedModal}
          onClose={() => setShowAdvancedModal(false)}
          onSave={(updatedConfig) => {
            setShowAdvancedModal(false);
            handleAdvancedConfigSave(updatedConfig);
          }}
          advancedSearchState={advancedSearchState}
          updateBoostConfig={updateBoostConfig}
          updateFuzzyConfig={updateFuzzyConfig}
          updateMatchPhraseConfig={updateMatchPhraseConfig}
          updateMatchPhrasePrefixConfig={updateMatchPhrasePrefixConfig}
          updateMinimumShouldMatch={updateMinimumShouldMatch}
          updateScriptScoreConfig={updateScriptScoreConfig}
          updateBoostMode={updateBoostMode}
          toggleAdvancedSearch={toggleAdvancedSearch}
          updateKeepSearchResults={updateKeepSearchResults}
          updateSearchLimit={updateSearchLimit}
          updateSearchFlyerItems={updateSearchFlyerItems}
          resetToDefaults={resetToDefaults}
          zIndex={zIndex + 2}
        />
      )}
    </Box>
  );
}
